import { z } from 'zod';
/**
 * LANGUAGES SPOKEN, as a closed list (#772; Julio, #582 decision 2, "fixed list for languages").
 *
 * HR picks each person's languages from this list, with "Other" and a short text for one the list does
 * not name, so the Roster can filter on a language without "Spanish", "spanish" and "Español" being
 * three different values.
 *
 * ONE CONSTANT, so the list changes in one place. The choices are the languages common among home-health
 * staff in Northern Virginia, alphabetically after English and Spanish. No document or code named a list
 * before this one, so it was proposed in #773 and Julio approved it as built ("yes to the language list", recorded on #772).
 *
 * IDS, NEVER LABELS, are stored (the same rule as `candidateSourceSchema`): a label is a thing design may
 * reword without meaning to re-key every record. `other` is last and is not a language, it is the way to
 * type one.
 */
export const LANGUAGE_CHOICES = [
    { id: 'english', label: 'English' },
    { id: 'spanish', label: 'Spanish' },
    { id: 'amharic', label: 'Amharic' },
    { id: 'arabic', label: 'Arabic' },
    { id: 'bengali', label: 'Bengali' },
    { id: 'chinese', label: 'Chinese (Mandarin or Cantonese)' },
    { id: 'farsi', label: 'Farsi' },
    { id: 'french', label: 'French' },
    { id: 'hindi', label: 'Hindi' },
    { id: 'korean', label: 'Korean' },
    { id: 'portuguese', label: 'Portuguese' },
    { id: 'tagalog', label: 'Tagalog' },
    { id: 'urdu', label: 'Urdu' },
    { id: 'vietnamese', label: 'Vietnamese' },
    { id: 'other', label: 'Other' },
];
const LANGUAGE_IDS = LANGUAGE_CHOICES.map((choice) => choice.id);
export const languageIdSchema = z.enum(LANGUAGE_IDS);
/** What HR reads for each stored id. */
export const languageLabels = Object.fromEntries(LANGUAGE_CHOICES.map((choice) => [choice.id, choice.label]));
/** Longest "Other" text: one or two language names, not a paragraph. */
export const MAX_OTHER_LANGUAGE_LENGTH = 100;
/**
 * A person's languages: the ticked ids, and what HR typed when "Other" is ticked.
 *
 * Three rules, and each of them has a way of being got wrong (the same three as the candidate source):
 *  - every id is one of the listed ones and is listed once;
 *  - "other" must carry the typed text, or it records that HR answered and says nothing;
 *  - without "other" there is no text, so a later edit cannot leave "Other" unticked beside a stale word.
 *
 * Strict, so an unknown key is refused rather than kept. The value is replaced as a whole on every save
 * (it is not one of the groups `applyPersonPatch` merges key by key), so the rules above are judged on
 * exactly what will be stored.
 */
export const personLanguagesSchema = z.object({
    spoken: z.array(languageIdSchema).max(LANGUAGE_CHOICES.length),
    other: z.string().trim().min(1).max(MAX_OTHER_LANGUAGE_LENGTH).nullable().default(null),
}).strict().superRefine((languages, context) => {
    if (new Set(languages.spoken).size !== languages.spoken.length) {
        context.addIssue({ code: 'custom', path: ['spoken'], message: 'List each language once.' });
    }
    const hasOther = languages.spoken.includes('other');
    if (hasOther && languages.other === null) {
        context.addIssue({ code: 'custom', path: ['other'], message: 'Say which language "Other" is.' });
    }
    if (!hasOther && languages.other !== null) {
        context.addIssue({ code: 'custom', path: ['other'], message: 'Only "Other" carries typed text.' });
    }
});
/** The ticked ids in the list's own order, whatever order they were ticked in, so two equal sets compare equal. */
export function orderedLanguages(spoken) {
    return LANGUAGE_CHOICES.map((choice) => choice.id).filter((id) => spoken.includes(id));
}
/** The words HR reads for a person's languages: "Spanish, English, Tigrinya", or null when none are recorded. */
export function languagesLine(languages) {
    if (languages === undefined || languages.spoken.length === 0)
        return null;
    return languages.spoken.map((id) => (id === 'other' ? languages.other ?? languageLabels.other : languageLabels[id])).join(', ');
}
/** Whether a person speaks the language a Roster filter names. */
export function speaksLanguage(languages, id) {
    return languages?.spoken.includes(id) === true;
}
//# sourceMappingURL=languages.js.map